// MoodTrends - Stimmungs-Visualisierung und Statistiken

import React, { useMemo, useState } from 'react';
import { TrendingUp, BarChart3, PieChart, Calendar } from 'lucide-react';
import type { PlaceNote } from '../../types';
import { Timestamp } from '@firebase/firestore';
import { EXTENDED_MOOD_OPTIONS, MOOD_CATEGORIES, getMoodByValue } from '../../constants/moods';
import type { LogbookMoodCategory } from '../../types';

interface MoodTrendsProps {
  notes: PlaceNote[];
}

type TimeRange = 'week' | 'month' | 'year' | 'all';

interface MoodCount {
  value: string;
  label: string;
  emoji: string;
  count: number;
  category: LogbookMoodCategory;
}

interface CategoryCount {
  id: LogbookMoodCategory;
  label: string;
  emoji: string;
  count: number;
  percentage: number;
}

function getDateFromTimestamp(date: Timestamp | Date | undefined): Date {
  if (!date) return new Date(0);
  if (date instanceof Timestamp) return date.toDate();
  return new Date(date);
}

function isWithinTimeRange(date: Date, range: TimeRange): boolean {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = diff / (1000 * 60 * 60 * 24);

  switch (range) {
    case 'week':
      return days <= 7;
    case 'month':
      return days <= 30;
    case 'year':
      return days <= 365;
    case 'all':
      return true;
  }
}

export function MoodTrends({ notes }: MoodTrendsProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [showDetails, setShowDetails] = useState(false);

  // Filter notes by time range
  const filteredNotes = useMemo(() => {
    return notes.filter((note) => {
      const date = getDateFromTimestamp(note.visitDate || note.createdAt);
      return isWithinTimeRange(date, timeRange);
    });
  }, [notes, timeRange]);

  // Calculate mood statistics
  const moodStats = useMemo(() => {
    const moodCounts = new Map<string, number>();
    let totalMoods = 0;

    filteredNotes.forEach((note) => {
      // Handle new moods array
      if (note.moods && note.moods.length > 0) {
        note.moods.forEach((mood) => {
          moodCounts.set(mood, (moodCounts.get(mood) || 0) + 1);
          totalMoods++;
        });
      }
      // Handle legacy mood field
      else if (note.mood) {
        moodCounts.set(note.mood, (moodCounts.get(note.mood) || 0) + 1);
        totalMoods++;
      }
    });

    // Convert to array with full mood info
    const moodArray: MoodCount[] = [];
    moodCounts.forEach((count, value) => {
      const mood = getMoodByValue(value);
      if (mood) {
        moodArray.push({
          value,
          label: mood.label,
          emoji: mood.emoji,
          count,
          category: mood.category,
        });
      }
    });

    // Sort by count (descending)
    moodArray.sort((a, b) => b.count - a.count);

    return { moodArray, totalMoods };
  }, [filteredNotes]);

  // Calculate category statistics
  const categoryStats = useMemo(() => {
    const categoryCounts = new Map<LogbookMoodCategory, number>();
    let total = 0;

    moodStats.moodArray.forEach((mood) => {
      categoryCounts.set(mood.category, (categoryCounts.get(mood.category) || 0) + mood.count);
      total += mood.count;
    });

    const categories: CategoryCount[] = MOOD_CATEGORIES.map((cat) => ({
      id: cat.id,
      label: cat.label,
      emoji: cat.emoji,
      count: categoryCounts.get(cat.id) || 0,
      percentage: total > 0 ? Math.round(((categoryCounts.get(cat.id) || 0) / total) * 100) : 0,
    }));

    return categories.filter((c) => c.count > 0);
  }, [moodStats]);

  // Top moods
  const topMoods = moodStats.moodArray.slice(0, 5);
  const maxCount = topMoods[0]?.count || 1;

  // Calculate average rating
  const avgRating = useMemo(() => {
    const ratedNotes = filteredNotes.filter((n) => n.personalRating);
    if (ratedNotes.length === 0) return null;
    const sum = ratedNotes.reduce((acc, n) => acc + (n.personalRating || 0), 0);
    return (sum / ratedNotes.length).toFixed(1);
  }, [filteredNotes]);

  if (filteredNotes.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
        <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Keine Daten für den gewählten Zeitraum</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            <h3 className="font-semibold">Stimmungs-Trends</h3>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-white/80 hover:text-white transition-colors"
          >
            {showDetails ? 'Weniger' : 'Details'}
          </button>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
          {(['week', 'month', 'year', 'all'] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                timeRange === range
                  ? 'bg-indigo-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {range === 'week' && '7 Tage'}
              {range === 'month' && '30 Tage'}
              {range === 'year' && '1 Jahr'}
              {range === 'all' && 'Gesamt'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 p-4 border-b border-gray-100">
        <div className="text-center">
          <p className="text-2xl font-bold text-indigo-600">{filteredNotes.length}</p>
          <p className="text-xs text-gray-500">Einträge</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-600">{moodStats.totalMoods}</p>
          <p className="text-xs text-gray-500">Stimmungen</p>
        </div>
        <div className="text-center">
          {avgRating ? (
            <>
              <p className="text-2xl font-bold text-amber-500">{avgRating}</p>
              <p className="text-xs text-gray-500">Ø Bewertung</p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-300">-</p>
              <p className="text-xs text-gray-500">Ø Bewertung</p>
            </>
          )}
        </div>
      </div>

      {/* Category Distribution */}
      <div className="p-4 border-b border-gray-100">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <PieChart className="w-4 h-4" />
          Kategorien
        </h4>
        <div className="space-y-2">
          {categoryStats.map((cat) => (
            <div key={cat.id} className="flex items-center gap-3">
              <span className="text-lg w-6">{cat.emoji}</span>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">{cat.label}</span>
                  <span className="text-sm text-gray-400">{cat.percentage}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      cat.id === 'positive' ? 'bg-green-400' :
                      cat.id === 'neutral' ? 'bg-blue-400' :
                      cat.id === 'mixed' ? 'bg-amber-400' :
                      cat.id === 'special' ? 'bg-purple-400' :
                      'bg-orange-400'
                    }`}
                    style={{ width: `${cat.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Moods Bar Chart */}
      <div className="p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Häufigste Stimmungen
        </h4>
        <div className="space-y-3">
          {topMoods.map((mood, index) => (
            <div key={mood.value} className="flex items-center gap-3">
              <span className="w-6 text-center text-gray-400 text-sm">{index + 1}.</span>
              <span className="text-lg">{mood.emoji}</span>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700">{mood.label}</span>
                  <span className="text-sm text-gray-400">{mood.count}x</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full transition-all duration-500"
                    style={{ width: `${(mood.count / maxCount) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Mood List (expandable) */}
      {showDetails && moodStats.moodArray.length > 5 && (
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Alle Stimmungen</h4>
          <div className="flex flex-wrap gap-2">
            {moodStats.moodArray.slice(5).map((mood) => (
              <span
                key={mood.value}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-full text-xs"
              >
                <span>{mood.emoji}</span>
                <span className="text-gray-600">{mood.label}</span>
                <span className="text-gray-400">({mood.count})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Mood over time hint */}
      {filteredNotes.length >= 5 && (
        <div className="px-4 py-3 bg-indigo-50 border-t border-indigo-100">
          <p className="text-xs text-indigo-600 text-center">
            {categoryStats[0]?.id === 'positive'
              ? '🎉 Deine Stimmungen sind überwiegend positiv!'
              : categoryStats[0]?.id === 'special'
                ? '✨ Du erlebst viele besondere Momente!'
                : categoryStats[0]?.id === 'energy'
                  ? '⚡ Energie ist dein häufigstes Thema!'
                  : `${categoryStats[0]?.emoji} ${categoryStats[0]?.label} dominiert deine Einträge`
            }
          </p>
        </div>
      )}
    </div>
  );
}

export default MoodTrends;
